import store from "/WebInterface/new-ui/upload/js/reducer.js";
import Tooltip from "/WebInterface/new-ui/modules/tooltip/tooltip.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import {
  openFile,
  closeFile,
  makeDir,
  checkFileExist,
  isFileOpen,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  handleFileOpenError,
  handleCloseFileRetryCommon,
  startFileUploadCommon,
  failFileOpenCommon,
  handleFileOpenResponse,
  handleRetryUploading,
  updateTimeStatusOfUploadingFileCommon,
  pausingUpload,
  pauseUpload,
  resumeUpload,
  closeFileUsingBlockUnBlockUpload,
  isMakeDirResponseValid,
} from "/WebInterface/new-ui/upload/js/common.js";
import uploadingQueue from "/WebInterface/new-ui/upload/js/uploading-queue.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";

class WSUploads {
  constructor({
    maxRetries,
    maxRequest = 10,
    WSUploadMaxRequests = 10,
    WSUploadDefaultChunkSize = 5,
    serverWriteThresholdWSUpload = 100,
    showServerWriteProgress = false,
    testRecovery = false,
    chunkPreparationTimeout = 480, // 8 minutes in seconds
  }) {
    console.log({
      maxRetries,
      maxRequest,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      serverWriteThresholdWSUpload,
      showServerWriteProgress,
      testRecovery,
      chunkPreparationTimeout,
    });
    this.showServerWriteProgress = showServerWriteProgress;
    this.serverWriteThreshold = serverWriteThresholdWSUpload
      ? parseInt(serverWriteThresholdWSUpload)
      : 100;
    this.maxRetries = maxRetries ? parseInt(maxRetries) : 30;
    this.maxRequest = WSUploadMaxRequests
      ? parseInt(WSUploadMaxRequests)
      : maxRequest;
    this.defaultChunkSize = WSUploadDefaultChunkSize
      ? parseInt(WSUploadDefaultChunkSize)
      : 5;
    this.testRecovery = testRecovery;
    this.chunkPreparationTimeout = chunkPreparationTimeout;
    this.initializeVariables();
    C.log(
      `Upload: Server write buffer size in MB: ${this.serverWriteThreshold}`
    );
    this.uploadSummary = {
      totalChunks: 0,
      averageUploadTime: 0,
      openFileTime: 0,
      closeFileTime: 0,
      socketInfo: [],
      sendChunksRejections: {
        noIdleSocket: 0,
        noReadyChunk: 0,
        serverWriteThresholdExceeded: 0,
      },
      chunkDetails: [],
      socketFailures: [], // Add socket failures tracking
    };
    this.sendChunksCallCount = 0;
    this.lastRandomCloseTime = Date.now();
    this.randomCloseInterval = 30000; // 30 seconds minimum between random closes
  }

  initializeVariables = () => {
    this.chunksInfo = {
      chunks: [],
      chunkPos: 0,
      totalChunksUploaded: 0,
      lastBytesUploaded: 0,
      bytesUploaded: 0,
      bytesSent: 0,
      elapsedTime: 0,
      intervalId: null,
      index: 0,
    };

    this.retries = 0;
    this.isPaused = false;
    this.currentChunkSize = this.defaultChunkSize;
    this.MB = 1048576;
    this.maxConnectionRetries = 2;
    this.maxCloseFileRetries = 10;
    this.waitForAllSocketBeforeStart = true;
    this.chunksSent = [];
    this.chunksAckd = [];
    this.chunksBeingRead = {};
    this.uploadStartTime = null;
    this.pauseLog = [];
    this.uploadDone = false;
    this.lastChunkSentTime = null;
    this.lastChunkSentIsRetry = false;
    this.sockets = [];
    this.chunkStart = 0;
    this.chunkNum = 0;
    this.fileClosed = false;
    this.connectionRetries = 0;

    //to know if we are stuck in chunk preparation
    this.lastChunkGeneratedTime = null;
    this.chunkPreparationTimeoutId = null;
    this.chunkPreparationStalled = false;
    this.chunkPreparationStallCheckInterval = null;

    //Related to the upload summary
    this.serverWriteUpdateInterval = null;
    this.totalSocketsRespawned = 0;
    this.totalChunkPreparationStalls = 0;
    this.totalRecoveryActions = 0;
    this.totalChunkResendAttempts = 0;
    this.totalStuckSocketIdentifications = 0;
    this.lastPeriodicCheckTime = null;
  };

  getChunk = (file, start, size) => file.slice(start, start + size);

  createBuffer = (chunkSize, chunkIndex, chunkDataBuffer) => {
    const buffer = new ArrayBuffer(4 + chunkSize);
    const dataView = new Uint8Array(buffer, 4);
    const indexView = new DataView(buffer);
    indexView.setUint32(0, chunkIndex + 1);
    dataView.set(new Uint8Array(chunkDataBuffer));
    return buffer;
  };

  getPercentage = (fileSize, bytesReceived) => {
    if (fileSize === 0) return 0;
    return Math.floor((bytesReceived / fileSize) * 100);
  };

  isSocketOpen = (socket) => {
    return socket && socket.readyState === WebSocket.OPEN;
  };

  removeSocketFromStorage = (socket) => {
    if (!socket) return;
    if (socket.removeEventListener) {
      const socketEvents = Object.getOwnPropertyNames(socket);
      for (const event of socketEvents) {
        if (event.startsWith("on") && typeof socket[event] === "function") {
          socket.removeEventListener(event.slice(2), socket[event]);
        }
      }
    }
    const chunksBeingSent = [...socket.chunksBeingSent];

    C.log(
      `Upload: Removing socket from storage, chunksBeingSent: ${chunksBeingSent.join(
        ","
      )}`
    );

    const index = this.sockets.indexOf(socket);
    if (index !== -1) {
      this.sockets.splice(index, 1);
      C.log(`Upload: Socket closed/released: ${socket}`);
    }
    if (this.isSocketOpen(socket)) {
      socket.close();
    }
  };

  createSocket = (cb = () => {}, retrySocket = false, index) => {
    if (this.isPaused || this.uploadDone || this.fileClosed) {
      C.warn(
        `Upload: Socket creation failed, because ${
          this.isPaused
            ? "upload is paused"
            : this.uploadDone
            ? "upload is done"
            : this.fileClosed
            ? "file is closed"
            : "unknown"
        }`
      );
      return;
    }
    if (typeof index !== "number") {
      index =
        this.sockets.reduce((max, socket) => Math.max(max, socket.index), 0) +
        1;
    }
    if (!retrySocket && this.sockets.length >= this.maxRequest) {
      C.warn(
        `Upload: Socket creation failed, because max sockets reached: ${this.maxRequest} and index: ${index} Length of sockets: ${this.sockets.length}, isRetrySocket: ${retrySocket}`
      );
      return;
    }
    C.log(
      `Upload: Socket creation started, index: ${index}, length of sockets: ${this.sockets.length}, isRetrySocket: ${retrySocket}`
    );
    const { hostname, port, protocol } = location;
    const ws_protocol = protocol.startsWith("https") ? "wss" : "ws";
    const { id: fileId, reUploadId } = this.streamingFileInfo;
    C.time(`Upload: Creating socket ${index}`);
    const openStartTime = Date.now();
    const socket = new WebSocket(
      `${ws_protocol}://${hostname}:${port}/U/${reUploadId || fileId}`
    );
    socket.binaryType = "arraybuffer";
    if (!retrySocket) {
      this.sockets.push(socket);
    } else {
      this.retrySocket = socket;
      socket.isRetrySocket = true;
    }
    socket.chunksBeingSent = [];
    socket.chunksAckd = [];
    socket.index = index || this.sockets.length - 1;

    socket.onopen = () => {
      if (this.isPaused || this.uploadDone || this.fileClosed) {
        socket.close();
        return;
      }
      C.timeEnd(`Upload: Creating socket ${index}`);
      const openEndTime = Date.now();
      socket.timeTakenToOpen = openEndTime - openStartTime;
      cb(socket);
      this.uploadSummary.socketInfo.push({
        index: socket.index,
        timeTakenToOpen: socket.timeTakenToOpen,
        chunksSent: 0,
      });
    };

    socket.onclose = (event) => {
      if (!this.fileClosed) {
        C.log(`Upload: Socket closed`);
        console.log(event, socket, { ...socket });
        // Add socket closure to summary
        this.uploadSummary.socketFailures.push({
          socketIndex: socket.index,
          type: "close",
          timestamp: new Date().toLocaleString(),
          details: {
            event: event,
            socket: {
              index: socket.index,
              chunksBeingSent: socket.chunksBeingSent,
              chunksAckd: socket.chunksAckd,
              bufferedAmount: socket.bufferedAmount,
            },
          },
        });
        this.removeSocketFromStorage(socket);
        this.handleError(socket);
      }
    };

    socket.onerror = (event) => {
      C.error("Upload: Socket error");
      console.log(event, socket, { ...socket });
      // Add socket failure to summary
      this.uploadSummary.socketFailures.push({
        socketIndex: socket.index,
        type: "error",
        timestamp: new Date().toLocaleString(),
        details: {
          event: event,
          socket: {
            index: socket.index,
            chunksBeingSent: socket.chunksBeingSent,
            chunksAckd: socket.chunksAckd,
            bufferedAmount: socket.bufferedAmount,
          },
        },
      });
      this.removeSocketFromStorage(socket);
      this.handleError(socket);
    };

    socket.onmessage = (event) => {
      this.handleMessageFromSocket(event, socket);
    };
  };

  initializeSockets = (cb = () => {}) => {
    C.success(
      `Upload: Initializing sockets, creating ${this.maxRequest} sockets`
    );
    C.success(`Upload: Default Chunk size: ${this.defaultChunkSize}`);
    C.success(`Upload: Server write buffer size: ${this.serverWriteThreshold}`);
    const totalSockets = this.sockets.length || 0;
    const socketsToCreate = this.maxRequest - totalSockets + 1;

    if (socketsToCreate <= 0) {
      cb();
      return;
    }

    if (this.waitForAllSocketBeforeStart) {
      let createdSockets = 0;
      const intervalId = setInterval(() => {
        if (createdSockets < socketsToCreate) {
          createdSockets++;
          this.createSocket(
            () => {
              if (createdSockets === socketsToCreate) {
                clearInterval(intervalId);
                cb();
              }
            },
            socketsToCreate - createdSockets === 0,
            createdSockets - 1
          );
        } else {
          clearInterval(intervalId);
        }
      }, 1);
    } else {
      Array.from({ length: socketsToCreate }, () => {
        this.createSocket(cb);
      });
    }
  };

  findMostIdleSocket = (isRetry) => {
    if (isRetry) {
      if (this.isSocketOpen(this.retrySocket)) {
        const emptySockets = this.sockets.filter(
          (s) =>
            this.isSocketOpen(s) &&
            s.bufferedAmount < 5 * this.MB &&
            s.chunksBeingSent.length < 5
        );

        if (this.retrySocket.bufferedAmount === 0) {
          C.log(
            `Upload: Its a retry and retry socket is empty, using retry socket`
          );
          return { socket: this.retrySocket, emptySockets };
        } else if (emptySockets.length > 0) {
          C.log(
            `Upload: Its a retry, retry socket is non-empty, using an empty socket`
          );
          return {
            socket: emptySockets[0],
            emptySockets: emptySockets.map((s) => ({
              socket: s,
              index: s.index,
            })),
          };
        } else {
          C.log(
            `Upload: Its a retry, using non-empty retry socket as no empty sockets available`
          );
          if (this.retrySocket.chunksBeingSent.length < 5) {
            return { socket: this.retrySocket };
          }
        }
      } else {
        C.warn(
          `Upload: Retry socket is not available or not open, finding idle socket`
        );
        return this.findMostIdleSocket();
      }
    }
    let mostIdleSocket = null;
    const emptySockets = [];
    if (typeof this.lastUsedSocket !== "number") {
      this.lastUsedSocket = -1;
    } else if (this.lastUsedSocket >= this.sockets.length - 1) {
      this.lastUsedSocket = -1;
    }
    this.sockets.forEach((socket, index) => {
      if (
        index > this.lastUsedSocket &&
        this.isSocketOpen(socket) &&
        !socket.renewed &&
        !socket.isStuck
      ) {
        if (socket.bufferedAmount === 0 && socket.chunksBeingSent.length < 1) {
          emptySockets.push({ socket, index });
        }
        if (
          !mostIdleSocket ||
          (socket.bufferedAmount < mostIdleSocket.bufferedAmount &&
            socket.chunksBeingSent.length <
              mostIdleSocket.chunksBeingSent.length)
        ) {
          mostIdleSocket = socket;
        }
      }
    });
    return { socket: mostIdleSocket, emptySockets };
  };

  isChunkEligibleToResend = (chunkIndex) => {
    const chunk = this.chunksInfo.chunks[chunkIndex];
    if (!chunk) return false;
    return chunk.status !== "done" &&
      chunk.status !== "failed" &&
      Date.now() - chunk.sent_at >
        (chunk.retries ? (chunk.retries + 2) * 60000 : 120000)
      ? chunk
      : undefined;
  };

  findChunksToResend = () => {
    const { chunksSent, chunksAckd } = this;
    if (!chunksSent.length) return null;

    // Create Set for O(1) lookups of acknowledged chunks
    const ackdSet = new Set(chunksAckd);

    // Get current time once
    const now = Date.now();

    // Sort chunks by sent_at time to prioritize oldest chunks
    const unackdChunks = chunksSent
      .filter((chunk) => !ackdSet.has(chunk))
      .map((chunk) => this.chunksInfo.chunks[chunk])
      .filter(
        (chunk) => chunk && chunk.status !== "done" && chunk.status !== "failed"
      )
      .sort((a, b) => (a.sent_at || 0) - (b.sent_at || 0));

    // Find first eligible chunk
    for (const chunk of unackdChunks) {
      const timeSinceSent = now - chunk.sent_at;
      const retryThreshold = chunk.retries
        ? (chunk.retries + 2) * 60000
        : 120000;

      if (timeSinceSent > retryThreshold) {
        this.totalChunkResendAttempts++;
        C.log(
          `Upload: Found a chunk to resend: ${
            chunk.index
          }, its not Acked since ${Math.floor(
            timeSinceSent / 1000
          )} seconds. This is its retries: ${
            chunk.retries > 0 ? chunk.retries : 1
          }`
        );
        return chunk;
      }
    }

    return null;
  };

  finishedUpload = async () => {
    this.uploadDone = true;
    this.closeFileStream(this.streamingFileInfo);
    this.closeSockets();
    this.sockets = [];
    this.stopAllIntervals();
  };

  stopPeriodicCheck = () => {
    if (this.periodicCheckInterval) {
      clearInterval(this.periodicCheckInterval);
      this.periodicCheckInterval = null;
    }
  };

  startPeriodicCheck = () => {
    let periodicCheckInterval = null;
    const periodicCheck = () => {
      if (this.isPaused || this.fileClosed) {
        if (periodicCheckInterval) {
          clearInterval(periodicCheckInterval);
          periodicCheckInterval = null;
        }
        console.log(
          "VVV: Upload: Periodic check",
          this.isPaused,
          this.fileClosed
        );
        return;
      }
      this.lastPeriodicCheckTime = Date.now();
      this.sendChunks();
    };
    periodicCheckInterval = setInterval(periodicCheck, 5000);
    periodicCheck();
    this.periodicCheckInterval = periodicCheckInterval;
    this.lastPeriodicCheckTime = Date.now();
  };

  handleMessageFromSocket = (event, socket) => {
    if (this.isErrorMessage(event.data)) {
      this.handleErrorMessage(event.data);
      return;
    }
    const chunkIndex = parseInt(event.data) - 1;
    if (this.isDuplicateAck(chunkIndex)) {
      return;
    }
    this.processAck(chunkIndex, socket);
    this.sendChunks();
  };

  isErrorMessage = (data) => {
    return typeof data === "string" && data.toLowerCase().startsWith("error");
  };

  handleErrorMessage = (errorMsg) => {
    if (errorMsg.indexOf("CHUNK_NUM_") >= 0) {
      this.retryChunk(errorMsg);
      return;
    }
    C.error(`Upload: Error from server: ${errorMsg}`);
    this.handleChunkError(errorMsg);
  };

  retryChunk = (errorMsg) => {
    const match = errorMsg.match(/CHUNK_NUM_(\d+)_/);
    const chunkToRetry = match ? match[1] : null;
    if (
      chunkToRetry &&
      !this.isPaused &&
      !this.uploadDone &&
      !this.fileClosed
    ) {
      const chunkIndex = parseInt(chunkToRetry) - 1;
      const chunk = this.chunksInfo.chunks[chunkIndex];
      chunk.status = "uploading";
      chunk.retries = chunk.retries - 1;
      const currentDate = new Date();
      const dateMinus30Mins = new Date(currentDate.getTime() - 30 * 60000);
      chunk.sent_at = dateMinus30Mins;
      C.error(
        `Upload: The Catch: ${errorMsg}, the chunk will be retried, index ${chunkIndex}`
      );
    }
  };

  isDuplicateAck = (chunkIndex) => {
    return (
      this.chunksAckd.indexOf(chunkIndex) !== -1 &&
      this.chunksSent.indexOf(chunkIndex) !== -1
    );
  };

  processAck = (chunkIndex, socket) => {
    this.chunksAckd.push(chunkIndex);
    socket.chunksAckd.push(chunkIndex);

    this.sockets.forEach((s) => {
      s.chunksBeingSent = s.chunksBeingSent.filter((x) => x !== chunkIndex);
    });

    if (this.retrySocket) {
      this.retrySocket.chunksBeingSent =
        this.retrySocket.chunksBeingSent.filter((x) => x !== chunkIndex);
    }

    const chunk = this.chunksInfo.chunks[chunkIndex];
    if (chunk) {
      const timeTaken = chunk.sent_at
        ? (Date.now() - chunk.sent_at) / 1000
        : null;
      chunk.timeTaken = timeTaken;
      if (chunk.real_sent_at) {
        chunk.timeTakenReal = (Date.now() - chunk.real_sent_at) / 1000;
      }
      if (timeTaken === null) {
        C.error(`Upload: Chunk sent time is null`, chunk);
      }

      const chunkDetail = this.uploadSummary.chunkDetails.find(
        (c) => c.index === chunkIndex
      );
      if (chunkDetail) {
        chunkDetail.timeTaken = timeTaken;
        chunkDetail.timeTakenReal = chunk.timeTakenReal;
        chunkDetail.retries = chunk.retries;
        chunkDetail.bufferringTime = chunk.bufferringTime;
      }

      this.logAck(chunkIndex, timeTaken);
      this.updateChunkStatus(chunkIndex);
      this.updateStore();
    } else {
      C.error(
        `Upload: Chunk ACKed by server, but its not in our list. ${chunkIndex}`
      );
    }
  };

  logAck = (chunkIndex, timeTaken) => {
    if (timeTaken > 30) {
      C.warn(
        `Upload: Ack: ${chunkIndex}, it took ${timeTaken} Seconds to upload and ${this.chunksInfo.chunks[chunkIndex].retries} Retries`
      );
    }
  };

  updateChunkStatus = (chunkIndex) => {
    const chunk = this.chunksInfo.chunks[chunkIndex];
    chunk.status = "done";
    chunk.chunkData = null;
    chunk.buffer = null;
    this.chunksInfo.chunkPos = chunkIndex;
    this.chunksInfo.totalChunksUploaded++;
    this.chunksInfo.bytesUploaded += chunk.size;
  };

  updateStore = () => {
    const file = this.streamingFileInfo;
    const { id: fileId } = file;
    store.dispatch("updateFileUploadStatus", {
      fileId,
      size: this.chunksInfo.bytesUploaded,
      maxSize: file.size,
    });
    if (this.chunksInfo.bytesUploaded >= file?.size) {
      C.success(`Upload: File upload completed, closing sockets`);
      this.finishedUpload();
    }
  };

  checkFileOpenStatus = async () => {
    if (this.fileClosed) {
      return false;
    }
    if (!this._fileOpenRequestQueue) {
      this._fileOpenRequestQueue = [];
      this._isFileOpenRequestInProgress = false;
    }
    if (this._isFileOpenRequestInProgress) {
      return new Promise((resolve) => {
        this._fileOpenRequestQueue.push(resolve);
      });
    }
    this._isFileOpenRequestInProgress = true;
    try {
      const fileInfo = this.streamingFileInfo;
      const { id: fileId, reUploadId } = fileInfo;
      const response = await isFileOpen({
        upload_id: reUploadId || fileId,
        random: Math.random(),
        chunkInfo: JSON.stringify({}),
      });
      const fileOpen = response.toLowerCase() === "open";

      if (!fileOpen && !this.fileClosed) {
        this.closeSockets();
        clearInterval(this.chunksInfo.intervalId);
        this.fileClosed = true;
        this.closeFileUsingBlockUnBlock(false, null, {
          status: "failed",
          uploadingError: response || t("failedFileClosed"),
        });

        handleFileOpenError(fileInfo, t("failedFileClosed"));
      }
      while (this._fileOpenRequestQueue.length > 0) {
        const resolve = this._fileOpenRequestQueue.shift();
        resolve(fileOpen);
      }

      return fileOpen;
    } catch (error) {
      while (this._fileOpenRequestQueue.length > 0) {
        const resolve = this._fileOpenRequestQueue.shift();
        resolve(false);
      }
      throw error;
    } finally {
      this._isFileOpenRequestInProgress = false;
    }
  };

  handleChunkError = async (err) => {
    try {
      if (
        err?.toLowerCase().indexOf("quota") >= 0 &&
        err?.toLowerCase().indexOf("exceeded") >= 0
      ) {
        C.error(`Upload: Received an error from server: ${err}`);
        this.closeSockets();
        clearInterval(this.chunksInfo.intervalId);
        C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);
        this.fileClosed = true;

        this.invoke("growl", [
          t("yourFileQuotaExceeded"),
          t("fileUploadingFailed"),
          4000,
          true,
        ]);
        this.stopFileUploadingQueueOnQuotaStorageExceeded(
          decodeURIComponent(response)
            .toLowerCase()
            .indexOf("no such upload open") >= 0
            ? decodeURIComponent(response)
            : false
        );
        return;
      }
      const fileOpen = await this.checkFileOpenStatus();
      if (!fileOpen) {
        return;
      }
      this.handleRetry(this.retries, () => {
        this.startUploading(this.chunkStart);
      });
    } catch (error) {
      C.error(error);
      this.invoke("growl", [error, "", 4000, true]);
      this.closeFileUsingBlockUnBlock(false, null, {
        status: "failed",
        uploadingError: t("somethingWentWrong"),
      });
    }
  };

  uploadHealthCheck = () => {
    if (!this.lastChunkSentTime) return;

    if (!this.lastLogTime) {
      this.lastLogTime = Date.now();
    }
    const shouldLog = Date.now() - this.lastLogTime > 10000;
    if (shouldLog) {
      this.logUploadStatus();
    }
  };

  //Upload Summary
  logUploadStatus = () => {
    this.lastLogTime = Date.now();
    const chunks = [...this.chunksInfo.chunks];
    const statusCounts = this.getChunkStatusCounts(chunks);
    const chunksYetToAck = this.getChunksToAck().sort((a, b) => a - b);
    C.warn(
      `Upload: Total chunks: ${chunks.length}, Pending chunks: ${statusCounts.init}, ` +
        `Done: ${statusCounts.done}, Failed: ${statusCounts.failed}, ` +
        `Uploading: ${statusCounts.uploading}, Retrying: ${statusCounts.retrying}, ` +
        `Chunks Sent: ${this.chunksSent.length}, Chunks Ackd: ${this.chunksAckd.length}, ` +
        `Sent but not Ackd: ${chunksYetToAck}, Current Chunk Size: ${this.currentChunkSize} MB`
    );
  };

  getChunksToAck = () => {
    return this.chunksSent.filter((value) => !this.chunksAckd.includes(value));
  };

  getSizeOfServerYetToWrite = () => {
    const chunksYetToAck = this.chunksSent.filter(
      (chunk) => !this.chunksAckd.includes(chunk)
    );

    const sizeYetToAck = chunksYetToAck.reduce((total, chunkIndex) => {
      const chunk = this.chunksInfo.chunks[chunkIndex];
      if (!chunk) {
        C.warn(`Upload: Chunk ${chunkIndex} not found in chunksInfo`);
        return total;
      }
      return total + chunk.size;
    }, 0);

    let chunksBeingSent = this.sockets.reduce((total, socket) => {
      return (
        total +
        socket.chunksBeingSent.reduce((socketTotal, chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          return socketTotal + (chunk?.size || 0);
        }, 0)
      );
    }, 0);

    if (this.retrySocket) {
      chunksBeingSent += this.retrySocket.chunksBeingSent.reduce(
        (total, chunkIndex) => {
          const chunk = this.chunksInfo.chunks[chunkIndex];
          return total + (chunk?.size || 0);
        },
        0
      );
    }
    const serverYetToWrite = (
      (sizeYetToAck + chunksBeingSent) /
      this.MB
    ).toFixed(2);

    return parseFloat(serverYetToWrite);
  };

  //Upload Summary
  updateServerWriteProgress = (serverYetToWrite) => {
    // if (this.isPaused || this.fileClosed || this.uploadDone) return;
    if (this.lastUpdateTime && Date.now() - this.lastUpdateTime < 500) {
      return;
    }

    this.lastUpdateTime = Date.now();

    const progressDiv = document.getElementById("server-write-progress-upload");
    if (!progressDiv) return;

    progressDiv.style.display = "block";

    this.updateRecoveryMessage(progressDiv);
    this.updateProgressBar(progressDiv, serverYetToWrite);
    this.updateSocketTable(progressDiv);
  };

  //Upload Summary
  updateRecoveryMessage = (progressDiv) => {
    let recoveryMessage = progressDiv.querySelector(".recovery-message");
    if (!recoveryMessage) {
      recoveryMessage = document.createElement("div");
      recoveryMessage.className = "recovery-message";
      Object.assign(recoveryMessage.style, {
        position: "absolute",
        top: "5px",
        right: "5px",
        backgroundColor: "rgba(255, 255, 0, 0.7)",
        padding: "2px 5px",
        borderRadius: "3px",
        fontSize: "12px",
        fontWeight: "normal",
        color: "black",
      });
      progressDiv.appendChild(recoveryMessage);
    }

    const recoveryReasons = [];
    if (!this.isPaused && !this.fileClosed && !this.uploadDone) {
      const stuckSockets = this.sockets.filter((socket) => socket.isStuck);
      if (stuckSockets.length > 0) {
        recoveryReasons.push(
          `Stuck sockets: ${stuckSockets.map((s) => s.index).join(", ")}`
        );
      }

      const retryingChunks = this.chunksInfo.chunks.filter(
        (chunk) => chunk.status !== "done" && chunk.retries > 0
      );
      if (retryingChunks.length > 0) {
        const chunkDetails = retryingChunks
          .map((chunk) => `${chunk.index} (${chunk.retries})`)
          .join(", ");
        recoveryReasons.push(`Retrying chunks: ${chunkDetails}`);
      }
    }

    const isRecovering = recoveryReasons.length > 0;
    recoveryMessage.textContent = isRecovering
      ? `Recovery: ${recoveryReasons.join(" | ")}`
      : "";
    recoveryMessage.style.display = isRecovering ? "block" : "none";
  };

  //Upload Summary
  updateProgressBar = (progressDiv, serverYetToWrite) => {
    const progressBar = progressDiv.querySelector(".progress-fill");
    const progressText = progressDiv.querySelector(".progress-text");
    const threshold = this.serverWriteThreshold;

    const percentage = Math.min((serverYetToWrite / threshold) * 100, 100);

    progressBar.style.width = `${percentage}%`;
    progressBar.style.backgroundColor =
      serverYetToWrite > threshold ? "#FF4136" : "#4CAF50";

    progressText.textContent = `Server write: ${serverYetToWrite.toFixed(
      2
    )} MB / ${threshold.toFixed(2)} MB`;
    if (serverYetToWrite > threshold) {
      progressText.textContent += ` (Extra ${(
        serverYetToWrite - threshold
      ).toFixed(2)} MB)`;
    }
  };

  //Upload Summary
  updateSocketTable = (progressDiv) => {
    let socketTable = progressDiv.querySelector(".socket-info-table");
    if (!socketTable) {
      socketTable = document.createElement("table");
      socketTable.className = "socket-info-table";
      Object.assign(socketTable.style, {
        width: "100%",
        marginTop: "10px",
        borderCollapse: "collapse",
      });
      progressDiv.appendChild(socketTable);

      // Add copy button
      const copyButton = document.createElement("button");
      copyButton.className = "copy-debug-info";
      copyButton.setAttribute(
        "data-tooltip",
        "Copy debug info (alt+cilck to download)"
      );
      copyButton.innerHTML = '<i class="icon-paste"></i>';
      Object.assign(copyButton.style, {
        position: "absolute",
        bottom: "5px",
        right: "5px",
        opacity: "0.7",
        padding: "5px 10px",
        color: "black",
        border: "none",
        borderRadius: "3px",
        cursor: "pointer",
        fontSize: "12px",
      });
      const tooltip = new Tooltip();

      copyButton.onclick = (event) => this.copyDebugInfo(event);
      progressDiv.appendChild(copyButton);
      _$(progressDiv)
        .find("[data-tooltip]")
        .sel()
        .forEach((button) => {
          tooltip.addTooltip({
            element: button,
            fixedPosition: true,
            position: "left",
          });
        });
    }

    const [indexRow, bufferRow] = this.getOrCreateRows(socketTable);
    this.updateSocketRows(indexRow, bufferRow);
    this.updateStatusRow(socketTable);
    this.updateSummaryRow(socketTable);
  };

  //Upload Summary
  getOrCreateRows = (table) => {
    const createRow = (className) => {
      const row = table.querySelector(`.${className}`) || table.insertRow();
      row.className = className;
      return row;
    };
    return [createRow("index-row"), createRow("buffer-row")];
  };

  //Upload Summary
  updateSocketRows = (indexRow, bufferRow) => {
    indexRow.innerHTML =
      "<td style='width: 80px; text-align: left;'>Socket:</td>";
    bufferRow.innerHTML =
      "<td style='width: 80px; text-align: left;'>Bfr:</td>";

    let totalBufferedAmount = 0;
    let totalChunksBeingSent = 0;

    const allSockets = [...this.sockets, this.retrySocket].filter(Boolean);
    const cellWidth = `${100 / (allSockets.length + 1)}%`;

    allSockets.forEach((socket, index) => {
      const socketBufferedAmount = socket.bufferedAmount / (1024 * 1024);
      totalBufferedAmount += socketBufferedAmount;
      totalChunksBeingSent += socket.chunksBeingSent.length;

      const socketLabel =
        socket === this.retrySocket ? "R" : index + "(" + socket.index + ")";
      this.createCell(
        indexRow,
        cellWidth,
        `<strong class="text-primaryMain">${socketLabel}</strong>`
      );
      this.createCell(
        bufferRow,
        cellWidth,
        `${socketBufferedAmount.toFixed(2)} (${socket.chunksBeingSent.length})`
      );
    });

    this.createCell(
      bufferRow,
      cellWidth,
      `${totalBufferedAmount.toFixed(2)} (${totalChunksBeingSent})`,
      true
    );
  };

  //Upload Summary
  createCell = (row, width, content, isTotal = false) => {
    const cell = row.insertCell();
    Object.assign(cell.style, {
      width,
      textAlign: "center",
      fontWeight: isTotal ? "bold" : "normal",
    });
    cell.innerHTML = content;
  };

  //Upload Summary
  updateStatusRow = (table) => {
    const chunksReadyInMemory = this.chunksInfo.chunks.filter(
      (chunk) => chunk.status === "init"
    ).length;
    const allChunksRead =
      this.chunksInfo.chunks.length > 0 &&
      this.chunksInfo.chunks[this.chunksInfo.chunks.length - 1].isLastChunk;

    let statusText = `${this.streamingFileInfo.name} (${
      this.streamingFileInfo.reUploadId || this.streamingFileInfo.id
    })`;
    statusText += `Chunks ready in memory: ${chunksReadyInMemory}`;
    if (allChunksRead) statusText += " | All chunks read";

    let statusRow = table.querySelector(".status-row") || table.insertRow();
    statusRow.className = "status-row";
    statusRow.innerHTML = `<td colspan="${
      this.sockets.length + 2
    }" style='text-align: left;'>${statusText}</td>`;
  };

  //Upload Summary
  updateSummaryRow = (table) => {
    // if (this.isPaused || this.fileClosed || this.uploadDone) return;

    const chunks = this.chunksInfo.chunks;
    const chunksYetToAck = this.getChunksToAck().sort((a, b) => a - b);
    const serverYetToWrite = this.getSizeOfServerYetToWrite();
    const socketInfo = this.getSocketInfo();

    // Get recovery information
    const recoveryReasons = [];
    if (!this.isPaused && !this.fileClosed && !this.uploadDone) {
      const stuckSockets = this.sockets.filter((socket) => socket.isStuck);
      if (stuckSockets.length > 0) {
        recoveryReasons.push(
          `Stuck sockets: ${stuckSockets.map((s) => s.index).join(", ")}`
        );
      }

      const retryingChunks = this.chunksInfo.chunks.filter(
        (chunk) => chunk.status !== "done" && chunk.retries > 0
      );
      if (retryingChunks.length > 0) {
        const chunkDetails = retryingChunks
          .map((chunk) => `${chunk.index} (${chunk.retries})`)
          .join(", ");
        recoveryReasons.push(`Retrying chunks: ${chunkDetails}`);
      }
    }

    // Get chunk status counts
    const statusCounts = this.getChunkStatusCounts(chunks);

    let summaryRow = table.querySelector(".summary-row") || table.insertRow();
    summaryRow.className = "summary-row";
    summaryRow.style.position = "relative"; // Add relative positioning to the row
    summaryRow.innerHTML = `
      <td colspan="${this.sockets.length + 2}" style='text-align: left;'>
        <span style="position: absolute;top: 5px;left: -8px;display: inline-block;width: 7px;height: 7px;border-radius: 50%; background-color: ${
          this.periodicCheckInterval &&
          Date.now() - this.lastPeriodicCheckTime < 6000
            ? "#4CAF50"
            : "#9E9E9E"
        };" title="${
      this.periodicCheckInterval &&
      Date.now() - this.lastPeriodicCheckTime < 6000
        ? "Periodic check active"
        : "Periodic check inactive"
    }"></span>
        Total: ${chunks.length} |
        Uploading: ${statusCounts.uploading} |
        In memory: ${chunks.filter((chunk) => chunk.status === "init").length} |
        Retrying: ${statusCounts.retrying} |
        Last generated ${
          this.lastChunkGeneratedTime
            ? Math.round((Date.now() - this.lastChunkGeneratedTime) / 1000)
            : 0
        } secs ago |
        Last sent ${
          this.lastChunkSentTime
            ? Math.round((Date.now() - this.lastChunkSentTime) / 1000)
            : 0
        } secs ago ${this.lastChunkSentIsRetry ? " (R)" : ""} |
        Sockets respawned: ${this.totalSocketsRespawned} <br>
        Chunk prep stalls: ${this.totalChunkPreparationStalls} |
        Recovery actions: ${this.totalRecoveryActions} |
        Chunk resend attempts: ${this.totalChunkResendAttempts} |
        Stuck socket identification: ${
          this.totalStuckSocketIdentifications
        } <br>
        Sent: ${this.chunksSent.length} |
        Ackd: ${this.chunksAckd.length} |
        Yet to ack: ${chunksYetToAck.length}
        ${
          chunksYetToAck.length > 0
            ? `(${chunksYetToAck
                .slice(0, 20)
                .map((chunkIndex) => {
                  const chunk = this.chunksInfo.chunks[chunkIndex];
                  return chunk && chunk.retries > 0
                    ? `${chunkIndex} (${chunk.retries})`
                    : chunkIndex;
                })
                .join(", ")}${chunksYetToAck.length > 20 ? "..." : ""})`
            : ""
        }
      </td>
    `;
  };

  //Upload Summary
  getChunkStatusCounts = (chunks) => {
    return chunks.reduce(
      (counts, chunk) => {
        counts[chunk.status]++;
        if (chunk.status !== "done" && chunk.retries > 0) counts.retrying++;
        return counts;
      },
      { done: 0, failed: 0, init: 0, uploading: 0, retrying: 0 }
    );
  };

  //Upload Summary
  logSocketInfo = () => {
    const chunkSize = this.MB * this.currentChunkSize;
    const socketInfo = this.sockets.map((socket) =>
      this.getIndividualSocketInfo(socket, chunkSize)
    );
    socketInfo.push(
      this.getIndividualSocketInfo(this.retrySocket, chunkSize, "Retry Socket")
    );
    // C.table(socketInfo);
    this.sockets.forEach((socket) => {
      C.log(
        `Upload: Socket ${socket.index}: Buffer: ${socket.bufferedAmount}, Chunks being sent: ${socket.chunksBeingSent?.length}, Chunks acked: ${socket.chunksAckd?.length}`
      );
    });
  };

  //Upload Summary
  getIndividualSocketInfo = (socket, chunkSize, index = socket.index) => {
    return {
      index,
      chunksBeingSent: socket.chunksBeingSent,
      chunksAckd: socket.chunksAckd,
      isStuck: socket.isStuck,
      beingSentVsAckd: `${(
        socket.chunksBeingSent.length / socket.chunksAckd.length
      ).toFixed(2)}`,
      buffer: `${(socket.bufferedAmount / 1048576).toFixed(2)} MB`,
      lastBufferedAmount: socket.lastBufferedAmount
        ? `${(socket.lastBufferedAmount / 1048576).toFixed(2)} MB`
        : null,
      lastCheckedTime: socket.lastCheckedTime
        ? `${(Date.now() - socket.lastCheckedTime) / 1000}`
        : null,
    };
  };

  closeSockets = () => {
    this.sockets.forEach((socket) => {
      if (this.isSocketOpen(socket)) {
        socket.close();
      }
    });
  };

  randomlyCloseSocket = () => {
    if (
      !this.testRecovery ||
      this.isPaused ||
      this.fileClosed ||
      this.uploadDone
    )
      return;

    const now = Date.now();
    if (now - this.lastRandomCloseTime < this.randomCloseInterval) return;

    // 5% chance to close all active sockets
    if (Math.random() < 0.05) {
      const openSockets = this.sockets.filter(
        (socket) =>
          this.isSocketOpen(socket) && socket.chunksBeingSent.length > 0
      );

      if (openSockets.length > 0) {
        C.warn(
          `Upload: Randomly closing all ${openSockets.length} active sockets for testing recovery`
        );
        this.lastRandomCloseTime = now;
        openSockets.forEach((socket) => {
          socket.close();
        });
      }
    }
  };

  sendChunks = (emptySocket) => {
    this.sendChunksCallCount++;
    this.getSizeOfServerYetToWrite();

    // Add random socket closing for testing
    this.randomlyCloseSocket();

    if (this.isPaused) {
      if (this.chunksSent.length === this.chunksAckd.length) {
        setTimeout(() => {
          this.pause();
        }, 1000);
      }
      return;
    }
    if (this.fileClosed || this.uploadDone || this.sockets.length <= 0) {
      return;
    }
    this.uploadHealthCheck();
    let readyChunk = this.findChunksToResend();
    let isRetry = !!readyChunk;
    if (readyChunk && readyChunk.status === "retry") {
      C.warn(
        `Upload: Resending chunk: ${readyChunk.index}, status: ${readyChunk.status}`
      );
      isRetry = true;
    }

    if (
      !isRetry &&
      this.getSizeOfServerYetToWrite() > this.serverWriteThreshold
    ) {
      this.uploadSummary.sendChunksRejections.serverWriteThresholdExceeded++;
      return;
    }

    const { socket, emptySockets } = this.findMostIdleSocket(isRetry);

    if (!socket) {
      C.warn(
        `Upload: No idle socket found, ${this.getSizeOfServerYetToWrite()}, threshold: ${
          this.serverWriteThreshold
        }`
      );
      if (!isRetry) {
        this.uploadSummary.sendChunksRejections.noIdleSocket++;
      }
      return;
    }

    const chunks = this.chunksInfo.chunks;

    if (!readyChunk) {
      readyChunk = chunks.find(
        (chunk) =>
          chunk.status === "init" && !this.chunksSent.includes(chunk.index)
      );
    }

    if (!readyChunk && !this.uploadSummary.allChunksCreated) {
      console.log(
        "!!!No Ready Chunk:",
        this.shouldItPrepareMoreChunks(),
        this.chunksBeingRead,
        chunks.find(
          (chunk) =>
            chunk.status === "init" && !this.chunksSent.includes(chunk.index)
        )
      );
      this.uploadSummary.sendChunksRejections.noReadyChunk++;
      this.prepareNextChunks();
      return;
    }

    if (!this.isSocketOpen(socket)) {
      C.warn(
        `Upload: Socket not open, ${this.getSizeOfServerYetToWrite()}, threshold: ${
          this.serverWriteThreshold
        }`
      );
      this.uploadSummary.sendChunksRejections.socketNotOpen++;
      return;
    }

    if (readyChunk && this.isSocketOpen(socket)) {
      let emptySocketToUse = emptySocket;
      if (emptySocket) {
        if (emptySocket instanceof WebSocket) {
          emptySocketToUse = emptySocket;
        } else if (
          emptySocket.socket &&
          emptySocket.socket instanceof WebSocket
        ) {
          emptySocketToUse = emptySocket.socket;
        }
      }
      const socketToUse =
        emptySocketToUse && emptySocketToUse.bufferedAmount <= 0
          ? emptySocketToUse
          : socket;
      socketToUse.send(readyChunk.buffer);
      socketToUse.chunksSent = socketToUse.chunksSent
        ? socketToUse.chunksSent++
        : 1;

      socketToUse.chunksBeingSent.push(readyChunk.index);
      this.lastUsedSocket = socketToUse.index;
      this.lastChunkSentTime = Date.now();
      this.lastChunkSentIsRetry = isRetry;

      if (!isRetry) {
        readyChunk.status = "uploading";
        readyChunk.sent_at = Date.now();
        this.chunksSent.push(readyChunk.index);
        this.chunksInfo.bytesSent += readyChunk.size;
        this.prepareNextChunks();
      } else {
        if (readyChunk.status === "retry") {
          readyChunk.status = "uploading";
          readyChunk.sent_at = Date.now();
        }
        if (!readyChunk.retries) readyChunk.retries = 0;
        readyChunk.retries += 1;
        readyChunk.real_sent_at = readyChunk.sent_at;
        readyChunk.sent_at = Date.now();
      }

      const socketInfo = this.uploadSummary.socketInfo.find(
        (s) => s.index === socket.index
      );
      if (socketInfo) {
        socketInfo.chunksSent++;
      }

      if (
        !this.uploadSummary.chunkDetails.find(
          (chunk) => chunk.index === readyChunk.index
        )
      ) {
        this.uploadSummary.chunkDetails.push({
          index: readyChunk.index,
          size: readyChunk.size,
          uploadStartTime: Date.now(),
        });
      }
    }
    if (!readyChunk) {
      this.prepareNextChunks();
    }

    if (!emptySocket && emptySockets && emptySockets.length > 0) {
      emptySockets.forEach((emptySocket) => this.sendChunks(emptySocket));
    }

    setTimeout(this.sendChunks, 200);
  };

  sendInitialChunks = () => {
    this.prepareNextChunks(() => {
      this.sendChunks();
    });
    this.startPeriodicCheck();
  };

  startUploading = (resumeLoc) => {
    this.chunkStart = resumeLoc;
    this.prepareNextChunks(() => {}, resumeLoc);
    this.initializeSockets(() => {
      this.sendInitialChunks();
    });
  };

  convertChunkDataToBuffer = (chunk) => {
    const this_parent = this;
    return new Promise((resolve = () => {}, reject = () => {}) => {
      if (!chunk) reject();
      const startTime = Date.now();
      const reader = new FileReader();
      if (chunk.chunkData instanceof Blob) {
        reader.readAsArrayBuffer(chunk.chunkData);
      } else {
        C.warn(
          `Upload: Chunk data is not of type Blob, it is ${typeof chunk.chunkData}, Chunk ID: ${
            chunk.index
          } Status: ${chunk.status}`
        );
        reject();
      }
      reader.onload = (event) => {
        const chunkDataBuffer = event.target.result;
        const buffer = this_parent.createBuffer(
          chunk.chunkData.size,
          chunk.index,
          chunkDataBuffer
        );
        chunk.buffer = buffer;
        chunk.bufferringTime = (Date.now() - startTime) / 1000;
        resolve();
      };
    });
  };

  shouldItPrepareMoreChunks = () => {
    const lastChunk = this.chunksInfo.chunks[this.chunksInfo.chunks.length - 1];
    const chunks = this.chunksInfo.chunks;
    let haveEnoughReady = false;
    let readyChunks = 0;
    for (let i = chunks.length - 1; i >= 0; i--) {
      if (chunks[i].status === "init") {
        readyChunks++;
        if (readyChunks >= 30) {
          haveEnoughReady = true;
          break;
        }
      }
    }
    if (haveEnoughReady || (lastChunk && lastChunk.isLastChunk)) {
      this.lastChunkGeneratedTime = Date.now();
      return false;
    }
    return true;
  };

  startChunkPreparationStallCheck = () => {
    if (this.chunkPreparationStallCheckInterval) {
      clearInterval(this.chunkPreparationStallCheckInterval);
    }

    this.chunkPreparationStallCheckInterval = setInterval(() => {
      if (!this.lastChunkGeneratedTime) return;

      const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
      const wasStalled = this.chunkPreparationStalled;
      this.chunkPreparationStalled = timeElapsed > 36;
      if (wasStalled !== this.chunkPreparationStalled) {
        if (this.chunkPreparationStalled) {
          C.error(
            `Upload: Chunk preparation stalled. No new chunks generated in ${timeElapsed.toFixed(
              0
            )} seconds`
          );
        }
        this.prepareNextChunks();
        this.sendChunks();
        const file = this.streamingFileInfo;
        store.dispatch("toggleChunkPreparationStalled", {
          fileId: file.id,
          chunkPreparationStalled: this.chunkPreparationStalled,
        });
      }
    }, 1000);
  };

  clearChunkPreparationStallCheck = () => {
    if (this.chunkPreparationStallCheckInterval) {
      clearInterval(this.chunkPreparationStallCheckInterval);
      this.chunkPreparationStallCheckInterval = null;
    }
    this.chunkPreparationStalled = false;
  };

  prepareNextChunks = async (cb = () => {}, resumeLoc) => {
    if (!this.shouldItPrepareMoreChunks()) {
      return;
    }

    if (!this.lastChunkGeneratedTime) {
      this.lastChunkGeneratedTime = Date.now();
      this.startChunkPreparationTimeoutCheck();
      this.startChunkPreparationStallCheck();
    }

    let resume = resumeLoc;
    const file = this.streamingFile;
    const chunks = this.chunksInfo.chunks;
    const lastChunk = chunks[chunks.length - 1] || {};
    let chunkSize = this.MB * this.currentChunkSize;
    const chunkNum =
      lastChunk && typeof lastChunk.index !== "undefined"
        ? lastChunk.index + 1
        : 0;
    const chunkStart =
      resume ||
      (lastChunk && typeof lastChunk.start !== "undefined"
        ? lastChunk.start + lastChunk.size
        : 0);
    if (this.chunksBeingRead[chunkNum]) {
      cb();
      return;
    }
    this.chunksBeingRead[chunkNum] = true;

    // Adjust chunkSize for the last chunk
    if (file.size < chunkStart + chunkSize) {
      chunkSize = file.size - chunkStart;
    }

    if (chunkSize <= 0) {
      C.log(
        `All chunks created, no more chunks to create, chunkSize: ${chunkSize}`
      );
      lastChunk.isLastChunk = true;
      this.uploadSummary.allChunksCreated = true;
      this.clearChunkPreparationTimeout();
      this.clearChunkPreparationStallCheck();
      cb();
      return;
    }
    let chunkData = this.getChunk(file, chunkStart, chunkSize);
    if (!chunkData) {
      C.warn(`Upload: Chunk data is null, Chunk ID: ${chunkNum}`);
      this.handleChunkPreparationFailure();
      return;
    }
    const chunk = {
      index: chunkNum,
      start: chunkStart,
      size: chunkSize,
      status: "init",
      chunkData,
      retries: 0,
    };
    await this.convertChunkDataToBuffer(chunk);
    cb();
    this.chunksInfo.chunks.push(chunk);
    this.lastChunkGeneratedTime = Date.now();
    delete this.chunksBeingRead[chunkNum];
    if (resume) resume = resume + chunkSize;
    if (this.shouldItPrepareMoreChunks()) {
      this.prepareNextChunks(cb);
    }
  };

  startChunkPreparationTimeoutCheck = () => {
    if (this.chunkPreparationTimeoutId) {
      clearTimeout(this.chunkPreparationTimeoutId);
    }

    const checkTimeout = () => {
      if (!this.lastChunkGeneratedTime) return;

      const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
      if (timeElapsed >= this.chunkPreparationTimeout) {
        this.handleChunkPreparationFailure();
      } else {
        this.chunkPreparationTimeoutId = setTimeout(checkTimeout, 10000);
      }
    };
    this.chunkPreparationTimeoutId = setTimeout(checkTimeout, 10000);
  };

  clearChunkPreparationTimeout = () => {
    if (this.chunkPreparationTimeoutId) {
      clearTimeout(this.chunkPreparationTimeoutId);
      this.chunkPreparationTimeoutId = null;
    }
    this.lastChunkGeneratedTime = null;
  };

  handleChunkPreparationFailure = () => {
    const timeElapsed = (Date.now() - this.lastChunkGeneratedTime) / 1000;
    C.error(
      `Upload: Chunk preparation stalled. No new chunks generated in ${timeElapsed.toFixed(
        0
      )} seconds`
    );
    this.stopAllIntervals();

    // Close all sockets
    this.closeSockets();

    // Mark the upload as failed
    const file = this.streamingFileInfo;
    handleFileOpenError(file, t("failedToPrepareFileChunksOperationStalled"));

    // Show error message to user
    this.invoke("growl", [
      t("failedToPrepareFileChunks"),
      t("operationStalled"),
      5000,
      true,
    ]);

    // Close the file stream
    this.closeFileUsingBlockUnBlock(false, null, {
      status: "failed",
      uploadingError: t("failedToPrepareFileChunksOperationStalled"),
    });

    // Move to next file in queue
    this.nextFileInQueue();
  };

  queueNextChunk = () => {
    if (this.isPaused || this.fileClosed) {
      return;
    }
    this.sendChunks();
  };

  handleError = async (socket) => {
    if (this.isPaused || this.fileClosed || this.retries > this.maxRetries) {
      return;
    }

    C.log(`Upload: Handle Error, Active Sockets: ${this.sockets.length}`);

    try {
      const fileOpen = await this.checkFileOpenStatus();
      if (!fileOpen) {
        return;
      }
      if (this.sockets.length === 0) {
        this.handleRetry(this.retries, () => {
          this.startUploading(this.chunkStart);
        });
      } else {
        C.log(
          `Upload: Handle Error, Active Sockets: ${this.sockets.length}, Max Requests: ${this.maxRequest}`
        );
        if (this.sockets.length >= this.maxRequest) return;
        this.createSocket(() => {
          this.totalSocketsRespawned++;
          C.log(`Upload: Socket created, starting upload`);
          this.sendChunks();
        }, socket.isRetrySocket);
      }
    } catch (error) {}
  };

  handleRetry = async (retries, cb) => {
    if (this.isPaused || this.fileClosed) {
      return;
    }
    const callback = async () => {
      this.closeSockets();
      this.sockets = [];
    };

    const retryCallback = () => {
      this.retries++;
    };

    handleRetryUploading(this, retries, cb, callback, retryCallback);
  };

  updateTimeStatusOfUploadingFile = (resumeLoc = 0) => {
    updateTimeStatusOfUploadingFileCommon(resumeLoc, this);
  };

  nextFileInQueue = (canceled) => {
    uploadingQueue.nextFileInQueue(canceled);
  };

  stopFileUploadingQueueOnQuotaStorageExceeded = (message) => {
    uploadingQueue.stopFileUploadingQueueOnQuotaStorageExceeded(message);
  };

  invoke = (method, params) => {
    if (parent.window[method]) {
      return parent.window[method].apply(this, params);
    }
  };

  uploadDir = async (nameF, isDir = false) => {
    let folder = nameF.split("/");

    if (folder.length < 3 && !isDir) {
      return;
    }

    folder.pop();
    folder = folder.join("/") + "/";
    folder = utils.encodeURILocal(folder);

    try {
      const resp = await checkFileExist({
        path: folder,
        random: Math.random(),
      });
      if (!resp) {
        C.log(`Upload: Making directory: ${folder}`);
        const makeDirResp = await makeDir({ path: folder });
        if (isMakeDirResponseValid(makeDirResp)) {
          C.log(`Upload: Directory created: ${folder}`);
        } else {
          C.error(`Upload: Error occurred during makeDir: ${makeDirResp}`);
          throw new Error(
            `Upload: Error occurred during makeDir: ${makeDirResp}`
          );
        }
      } else {
        C.log(`Upload: Directory already exists: ${folder}`);
      }
    } catch (error) {
      C.error(`Upload:Error occurred during makeDir: ${error}`);
      throw error;
    }
  };

  checkFilePartiallyUploaded = async (file) => {
    const { nameF, resumed } = file;

    if (!resumed) {
      return 0;
    }

    try {
      const resp = await checkFileExist({
        path: nameF,
        random: Math.random(),
      });
      return resp;
    } catch (error) {
      C.error(`Upload:Error occurred during checkFileExist: ${error}`);
      throw error;
    }
  };

  beforeUnloadEvent = (event) => {
    event.returnValue = "";
  };

  // open file stream - creating bucket to upload chunks
  openFileStream = async (file) => {
    this.initializeVariables();
    this.setInitialUploadState(file);

    if (file.isDir) {
      await this.handleDirectoryUpload(file);
      return;
    }

    try {
      C.time(`Upload: Checking file partially uploaded`);
      const resp = await this.checkFilePartiallyUploaded(file);
      let resumeLoc = 0;
      if (!resp) {
        resumeLoc = 0;
      } else {
        let stats = resp.split(" ");
        stats = stats.filter((item) => item !== "");
        resumeLoc = parseInt(stats[4]);
        if (resumeLoc >= file.size) {
          resumeLoc = 0;
        }
        this.chunksInfo.bytesUploaded = resumeLoc;
      }

      C.timeEnd(`Upload: Checking file partially uploaded`);
      C.time(`Upload: Uploading directory`);
      try {
        await this.uploadDir(file.nameF);
      } catch (err) {
        console.error("Failed to upload directory:", err);
        this.nextFileInQueue(true);
        handleFileOpenError(file, t("failedToCreateDirectory"));
        C.timeEnd(`Upload: Uploading directory`);
        return;
      }
      C.timeEnd(`Upload: Uploading directory`);
      C.time(`Upload: Opening file for upload`);
      await this.openFileForUpload(file, resumeLoc);
      C.timeEnd(`Upload: Opening file for upload`);
    } catch (error) {
      handleFileOpenError(file, error);
    }
  };

  setInitialUploadState = (file) => {
    this.retries = 0;
    this.isPaused = false;
    this.closeFileRetries = 0;
    if (!this.uploadStartTime) {
      this.uploadStartTime = Date.now();
    }
    this.uploadingStartedAt = Date.now();
    this.streamingFileInfo = file;
    this.streamingFile = file.file;
  };

  stopAllIntervals = () => {
    this.clearChunkPreparationStallCheck();
    this.stopServerWriteUpdates();
    this.stopPeriodicCheck();
    this.clearChunkPreparationTimeout();

    this.lastChunkGeneratedTime = null;
    this.lastChunkSentTime = null;
    this.lastLogTime = null;
    this.bufferedAmountUnchangedSince = null;
    this.lastBufferedAmounts = null;
    this.lastPeriodicCheckTime = null;

    clearInterval(this.chunksInfo.intervalId);
    clearInterval(this.chunkRetryInterval);
  };

  handleDirectoryUpload = async (file) => {
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        status: "uploading",
      },
    });

    try {
      await this.uploadDir(file.nameF, file.isDir);
      this.completeDirectoryUpload(file);
    } catch (error) {
      handleFileOpenError(file, error);
    }
  };

  completeDirectoryUpload = (file) => {
    this.uploadingEndedAt = Date.now();
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        status: "done",
        time: this.uploadingEndedAt - this.uploadingStartedAt,
      },
    });
    this.nextFileInQueue();
  };

  openFileForUpload = async (file, resumeLoc) => {
    const startTime = Date.now();
    this.streamingFileInfo = file;
    this.streamingFile = file.file;
    const customFormData = this.streamingFileInfo?.customFormData || {};
    const formValues = store?.customCommonUploadFormValues || {};

    try {
      this.openFileStartTime = Date.now();
      const metaUserInfo = this.invoke("getMetaUserInfo");
      const response = await openFile({
        ...formValues,
        ...customFormData,
        ...metaUserInfo,
        upload_path: file.nameF,
        upload_size: file.size,
        upload_id: file?.reUploadId || file.id,
        start_resume_loc: resumeLoc,
      });
      this.openFileEndTime = Date.now();

      this.uploadSummary.openFileTime = Date.now() - startTime;
      handleFileOpenResponse(this, file, response, resumeLoc);
    } catch (err) {
      this.openFileEndTime = Date.now();
      handleFileOpenError(file, err);
    }
  };

  failFileOpen = (file, response) => {
    failFileOpenCommon(this, file, response);
  };

  startFileUpload = (file, resumeLoc) => {
    startFileUploadCommon(this, file, resumeLoc);
    this.startServerWriteUpdates();
  };

  handleCloseFileResponse = async (
    response,
    file,
    fileInfo,
    finalizingTimeoutId
  ) => {
    response =
      typeof response === "string" ? decodeURIComponent(response) : response;
    if (response.startsWith("Decompressing")) {
      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
        },
      });
      return { shouldRetry: true };
    } else if (response.startsWith("Finalizing")) {
      const pendingBytesMatch = response.match(/Pending bytes: (\d+)/);
      let finalizingMsg = "Finalizing";

      if (pendingBytesMatch && pendingBytesMatch[1]) {
        const pendingBytes = parseInt(pendingBytesMatch[1]);
        const totalBytes = file.size;
        const pendingFormatted = utils.formatBytes(pendingBytes);
        const totalFormatted = utils.formatBytes(totalBytes);
        finalizingMsg = `Finalizing (Pending: ${pendingFormatted}/${totalFormatted})`;
      }

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file.id]: {
          ...fileInfo,
          status: "finalizing",
          finalizingMsg: finalizingMsg,
        },
      });
      return { shouldRetry: true };
    } else if (response.indexOf("ERROR:") >= 0) {
      if (
        (response.toLowerCase().indexOf("quota") >= 0 &&
          response.toLowerCase().indexOf("exceeded") >= 0) ||
        decodeURIComponent(response)
          .toLowerCase()
          .indexOf("no such upload open") >= 0
      ) {
        this.stopAllIntervals();
        handleFileOpenError(file, decodeURIComponent(response));
        this.invoke("growl", [
          decodeURIComponent(response),
          t("fileClosingFailed"),
          4000,
          true,
        ]);
        this.stopFileUploadingQueueOnQuotaStorageExceeded(
          decodeURIComponent(response)
            .toLowerCase()
            .indexOf("no such upload open") >= 0
            ? decodeURIComponent(response)
            : false
        );
        return { shouldRetry: false };
      }
      this.uploadSummary.closeFileTime = Date.now() - startTime;
      this.logFinalUploadSummary();
      document.querySelector("#server-write-progress-upload").style.display =
        "none";
      this.handleCloseFileRetry(response);
      return { shouldRetry: false };
    }

    clearInterval(this.chunkRetryInterval);
    clearTimeout(finalizingTimeoutId);

    this?.sockets?.forEach((socket) => {
      if (this.isSocketOpen(socket)) {
        socket?.close();
      }
    });

    removeEventListener("beforeunload", this.beforeUnloadEvent);

    this.uploadingEndedAt = Date.now();
    const updatedFile = {
      ...file,
      status: "done",
      time: this.uploadingEndedAt - this.uploadingStartedAt,
    };

    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: updatedFile,
    });

    clearInterval(this.chunksInfo.intervalId);
    C.log(`Upload: Clearing interval: ${this.chunksInfo.intervalId}`);

    this.nextFileInQueue();
    return { shouldRetry: false };
  };

  closeFileStream = async (file) => {
    const startTime = Date.now();
    const { id: fileId, nameF, size, lastModified, reUploadId } = file;
    const fileInfo = this.streamingFileInfo;

    if (this.fileClosed) return;
    this.fileClosed = true;

    const finalizingTimeoutId = setTimeout(() => {
      const updatedFileToCanceling = {
        ...fileInfo,
        status: "finalizing",
        finalizingMsg: "Finalizing",
      };

      store.dispatch("updateFileStatusAndRerenderFile", {
        [fileId]: updatedFileToCanceling,
      });

      store.dispatch("updateFileTimeStatus", {
        fileId,
        speed: 0,
        elapsedTime: this.chunksInfo.elapsedTime,
      });

      store.dispatch("updateFileUploadStatus", {
        fileId,
        size: this.chunksInfo.bytesUploaded,
        maxSize: size,
      });
    }, 3000);

    try {
      const chunks_arr_length = this.chunksInfo.chunks.length;
      const total_chunks = !chunks_arr_length && size ? 1 : chunks_arr_length;

      const response = await closeFile({
        upload_id: reUploadId || fileId,
        total_chunks,
        total_bytes: size,
        filePath: nameF,
        lastModified: lastModified,
      });
      C.log(`Upload: File close response: ${response}`);

      const { shouldRetry } = await this.handleCloseFileResponse(
        response,
        file,
        fileInfo,
        finalizingTimeoutId
      );

      if (shouldRetry) {
        const retryCloseFile = async () => {
          try {
            const retryResponse = await closeFile({
              upload_id: reUploadId || fileId,
              total_chunks,
              total_bytes: size,
              filePath: nameF,
              lastModified: lastModified,
            });

            C.log(`Upload: File close retry response: ${retryResponse}`);
            const { shouldRetry: shouldRetryAgain } =
              await this.handleCloseFileResponse(
                retryResponse,
                file,
                fileInfo,
                finalizingTimeoutId
              );

            if (shouldRetryAgain) {
              setTimeout(retryCloseFile, 2000);
            }
          } catch (err) {
            clearTimeout(finalizingTimeoutId);
            clearInterval(this.chunkRetryInterval);
            this.handleCloseFileRetry();
            this.invoke("growl", [
              "",
              t("somethingWentWrongWhileClosingFile"),
              5000,
              true,
            ]);
            C.error(`Upload:Error occurred during closeFile retry, ${err}`);
          }
        };

        setTimeout(retryCloseFile, 2000);
        return;
      }
    } catch (err) {
      clearTimeout(finalizingTimeoutId);
      clearInterval(this.chunkRetryInterval);
      this.handleCloseFileRetry();
      this.invoke("growl", [
        "",
        t("somethingWentWrongWhileClosingFile"),
        5000,
        true,
      ]);
      C.error(`Upload:Error occurred during closeFile, ${err}`);
    }

    this.uploadSummary.closeFileTime = Date.now() - startTime;
    this.logFinalUploadSummary();
    document.querySelector("#server-write-progress-upload").style.display =
      "none";
  };

  handleCloseFileRetry = async (err) => {
    await handleCloseFileRetryCommon(this, err);
  };

  pausing = () => {
    pausingUpload(this);
  };

  pause = () => {
    pauseUpload(this);
    this.stopServerWriteUpdates();
    this.stopPeriodicCheck();
  };

  resume = () => {
    resumeUpload(this, this.queueNextChunk);
    this.startServerWriteUpdates();
    this.startPeriodicCheck(); // Restart periodic check
  };

  getSocketInfo = () => {
    return this.sockets.map((socket) => ({
      index: socket.index,
      chunksBeingSent: socket.chunksBeingSent.length,
      bufferSize: (socket.bufferedAmount / this.MB).toFixed(2),
      chunksAcked: socket.chunksAckd.length,
      isStuck: socket.isStuck,
    }));
  };

  closeFileUsingBlockUnBlock = (
    preStatus = {
      status: "canceling",
    },
    postStatus = {
      status: "canceled",
    }
  ) => {
    const beforeClosingFile = async () => {
      this.stopAllIntervals();
      this.closeSockets();
    };

    closeFileUsingBlockUnBlockUpload(
      preStatus,
      postStatus,
      beforeClosingFile,
      this
    );
  };

  cancel = async () => {
    C.error(
      `Upload: User canceled, Canceling file: ${this.streamingFileInfo.nameF}`
    );
    this.stopAllIntervals();
    this.closeFileUsingBlockUnBlock();
  };

  uploadTheNextSetOfChunk = (resumeLoc) => {
    this.startUploading(resumeLoc);
    this.handleUpdateUploadingStatus(true);
  };

  handleUpdateUploadingStatus = (uploading) => {
    uploadingQueue.handleUpdateUploadingStatus(uploading);
  };

  logFinalUploadSummary = () => {
    const totalUploadTime = this.uploadSummary.chunkDetails.reduce(
      (sum, chunk) => sum + (chunk.timeTaken || 0),
      0
    );
    this.uploadSummary.averageUploadTime =
      totalUploadTime / this.uploadSummary.chunkDetails.length;

    this.uploadSummary.totalChunks = this.uploadSummary.chunkDetails.length;

    const chunksWithoutRetries = [];
    const chunksWithRetries = [];

    this.uploadSummary.chunkDetails.forEach((chunk) => {
      const chunkInfo = {
        index: chunk.index,
        size: `${(chunk.size / this.MB).toFixed(2)} MB`,
        retries: chunk.retries,
        bufferringTime: chunk.bufferringTime
          ? `${chunk.bufferringTime.toFixed(2)} seconds`
          : "N/A",
        timeTakenReal: chunk.timeTakenReal
          ? `${chunk.timeTakenReal.toFixed(2)} seconds`
          : "N/A",
        timeTaken: chunk.timeTaken
          ? `${chunk.timeTaken.toFixed(2)} seconds`
          : "N/A",
      };

      if (!chunk.retries) {
        chunksWithoutRetries.push(chunkInfo);
      } else {
        chunksWithRetries.push(chunkInfo);
      }
    });

    // Format socket failures for summary
    const socketFailures = this.uploadSummary.socketFailures.map((failure) => ({
      socketIndex: failure.socketIndex,
      type: failure.type,
      timestamp: failure.timestamp,
      details: {
        chunksBeingSent: failure.details.socket.chunksBeingSent,
        chunksAckd: failure.details.socket.chunksAckd,
        bufferedAmount: `${(
          failure.details.socket.bufferedAmount / this.MB
        ).toFixed(2)} MB`,
      },
    }));

    const summary = {
      totalChunks: this.uploadSummary.totalChunks,
      averageUploadTime: `${this.uploadSummary.averageUploadTime.toFixed(
        2
      )} seconds`,
      openFileTime: `${(this.uploadSummary.openFileTime / 1000).toFixed(
        2
      )} seconds`,
      closeFileTime: `${(this.uploadSummary.closeFileTime / 1000).toFixed(
        2
      )} seconds`,
      socketInfo: this.uploadSummary.socketInfo.map((socket) => ({
        index: socket.index,
        timeTakenToOpen: `${(socket.timeTakenToOpen / 1000).toFixed(
          2
        )} seconds`,
        chunksSent: socket.chunksSent,
      })),
      sendChunksCallCount: this.sendChunksCallCount,
      sendChunksRejections: this.uploadSummary.sendChunksRejections,
      chunksWithoutRetries,
      chunksWithRetries,
      socketFailures, // Add socket failures to summary
    };

    C.log("Final Upload Summary:", summary);
    const debugText = this.formatDebugInfo();
    C.log("Detailed Summary:", debugText);
  };

  startServerWriteUpdates = () => {
    if (this.showServerWriteProgress) {
      if (this.serverWriteUpdateInterval) {
        clearInterval(this.serverWriteUpdateInterval);
      }
      this.serverWriteUpdateInterval = setInterval(() => {
        // if (this.isPaused || this.fileClosed || this.uploadDone) {
        //   this.stopServerWriteUpdates();
        //   return;
        // }

        const serverYetToWrite = this.getSizeOfServerYetToWrite();
        this.updateServerWriteProgress(serverYetToWrite);
      }, 1000);
    }
  };

  stopServerWriteUpdates = () => {
    if (this.showServerWriteProgress) {
      if (this.serverWriteUpdateInterval) {
        clearInterval(this.serverWriteUpdateInterval);
        this.serverWriteUpdateInterval = null;
      }
      const progressDiv = document.getElementById(
        "server-write-progress-upload"
      );
      if (progressDiv) {
        progressDiv.style.display = "none";
      }
    }
  };

  copyDebugInfo = (event) => {
    const debugInfo = this.formatDebugInfo();

    // Check if alt key is pressed
    if (event && event.altKey) {
      // Create filename with date-time
      const now = new Date();
      const dateTime = now
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "-")
        .slice(0, 19);
      const filename = `file_upload_stats-${dateTime}.txt`;

      // Create and download file
      const blob = new Blob([debugInfo], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      this.invoke("growl", [
        t("debugInformationDownloadedAsFile"),
        "",
        2000,
        false,
      ]);
    } else {
      // Original clipboard copy behavior
      navigator.clipboard
        .writeText(debugInfo)
        .then(() => {
          this.invoke("growl", [
            t("debugInformationCopiedToClipboard"),
            "",
            2000,
            false,
          ]);
        })
        .catch((err) => {
          console.error("Failed to copy debug info:", err);
          this.invoke("growl", [
            t("failedToCopyDebugInformation"),
            "",
            2000,
            true,
          ]);
        });
    }
  };

  formatDebugInfo = () => {
    const chunks = this.chunksInfo.chunks;
    const chunksYetToAck = this.getChunksToAck().sort((a, b) => a - b);
    const serverYetToWrite = this.getSizeOfServerYetToWrite();
    const socketInfo = this.getSocketInfo();

    // Get progress status from UI
    const fileId = this.streamingFileInfo?.id;
    console.log("fileId", fileId);
    const progressStatus = fileId
      ? document.querySelector(
          `#uploadFileSection div#${fileId} .upload-progress-status`
        )?.textContent
      : "N/A";

    // Format time difference
    const formatTimeDifference = (timestamp) => {
      if (!timestamp) return "N/A";
      const seconds = Math.floor((Date.now() - timestamp) / 1000);
      if (seconds < 1) return "just now";
      if (seconds < 60) return `${seconds} seconds ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes === 1) return "1 minute ago";
      return `${minutes} minutes ago`;
    };

    // Get recovery information
    const recoveryReasons = [];
    if (!this.isPaused && !this.fileClosed && !this.uploadDone) {
      const stuckSockets = this.sockets.filter((socket) => socket.isStuck);
      if (stuckSockets.length > 0) {
        recoveryReasons.push(
          `Stuck sockets: ${stuckSockets.map((s) => s.index).join(", ")}`
        );
      }

      const retryingChunks = this.chunksInfo.chunks.filter(
        (chunk) => chunk.status !== "done" && chunk.retries > 0
      );
      if (retryingChunks.length > 0) {
        const chunkDetails = retryingChunks
          .map((chunk) => `${chunk.index} (${chunk.retries})`)
          .join(", ");
        recoveryReasons.push(`Retrying chunks: ${chunkDetails}`);
      }
    }

    // Get chunk status counts
    const statusCounts = this.getChunkStatusCounts(chunks);

    const debugText = [
      "=== Upload Debug Information ===",
      `Username: ${this.invoke("getUserInfo")?.username || "Unknown"}`,
      `URL: ${window.parent.location.href || window.location.href}`,
      `Time: ${new Date().toLocaleString()}`,
      `File: ${this.streamingFileInfo?.nameF || "Unknown"}`,
      `File ID: ${
        this.streamingFileInfo?.reUploadId ||
        this.streamingFileInfo?.id ||
        "Unknown"
      }`,
      `Size: ${utils.formatBytes(this.streamingFileInfo?.size || 0)}`,
      `Upload started: ${new Date(this.uploadStartTime).toLocaleString()}`,
      `Last periodic check: ${formatTimeDifference(
        this.lastPeriodicCheckTime
      )}`,
      `Progress info: ${progressStatus}`,
      "",
      "=== Socket Information ===",
      ...socketInfo.map(
        (socket) =>
          `Socket ${socket.index}: Buffer: ${
            socket.bufferSize
          }MB, Chunks being sent: ${socket.chunksBeingSent}, Chunks acked: ${
            socket.chunksAcked
          }, Stuck: ${socket.isStuck ? "Yes" : "No"}`
      ),
      `Sockets respawned: ${this.totalSocketsRespawned}`,
      `Total stuck socket identifications: ${this.totalStuckSocketIdentifications}`,
      "",
      "=== Recovery Status ===",
      recoveryReasons.length > 0
        ? `Recovery in progress: ${recoveryReasons.join(" | ")}`
        : "No recovery actions in progress",
      `Total recovery actions: ${this.totalRecoveryActions}`,
      `Total chunk preparation stalls: ${this.totalChunkPreparationStalls}`,
      `Total chunk resend attempts: ${this.totalChunkResendAttempts}`,
      "",
      "=== Stuck Socket Details ===",
      ...this.sockets
        .filter((socket) => socket.isStuck)
        .map(
          (socket) =>
            `Socket ${socket.index}: Buffer: ${socket.bufferedAmount}MB, Chunks being sent: ${socket.chunksBeingSent}, Last checked: ${socket.lastCheckedTime}s ago`
        ),
      "",
      "=== Chunk Status ===",
      `Total chunks: ${chunks.length}`,
      `Chunks ready in memory: ${statusCounts.init}`,
      `Chunks uploading: ${statusCounts.uploading}`,
      `Chunks done: ${statusCounts.done}`,
      `Chunks failed: ${statusCounts.failed}`,
      `Chunks retrying: ${statusCounts.retrying}`,
      `Chunks sent: ${this.chunksSent.length}`,
      `Chunks sent with retries: ${
        this.chunksSent.filter((chunk) => chunk.status === "retry").length
      }`,
      `Chunks acknowledged: ${this.chunksAckd.length}`,
      `Chunks acknowledged with retries: ${
        this.chunksAckd.filter((chunk) => chunk.status === "retry").length
      }`,
      `Chunks yet to ack: ${chunksYetToAck.length}`,
      `Chunks yet to ack with retries: ${
        chunksYetToAck.filter((chunk) => chunk.status === "retry").length
      }`,
      "",
      "=== Server Write Status ===",
      `Server yet to write: ${serverYetToWrite.toFixed(2)}MB`,
      `Server write threshold: ${this.serverWriteThreshold}MB`,
      `Server write threshold exceeded: ${
        serverYetToWrite > this.serverWriteThreshold ? "Yes" : "No"
      }`,
      "",
      "=== Upload Status ===",
      `Status: ${
        this.isPaused
          ? "Paused"
          : this.fileClosed
          ? "Closed"
          : this.uploadDone
          ? "Done"
          : "Uploading"
      }`,
      `Bytes uploaded: ${utils.formatBytes(this.chunksInfo.bytesUploaded)}`,
      `Bytes sent: ${utils.formatBytes(this.chunksInfo.bytesSent)}`,
      `Last chunk generated: ${
        this.lastChunkGeneratedTime
          ? Math.floor((Date.now() - this.lastChunkGeneratedTime) / 1000)
          : 0
      } seconds ago`,
      `Last chunk sent: ${
        this.lastChunkSentTime
          ? Math.floor((Date.now() - this.lastChunkSentTime) / 1000)
          : 0
      } seconds ago`,
      `Last chunk sent was retry: ${this.lastChunkSentIsRetry ? "Yes" : "No"}`,
      `Chunk preparation stalled: ${
        this.chunkPreparationStalled ? "Yes" : "No"
      }`,
      "",
      "=== Retry Information ===",
      `On error retries: ${this.retries}`,
      `Max retries: ${this.maxRetries}`,
      `Current chunk size: ${this.currentChunkSize}MB`,
      `Default chunk size: ${this.defaultChunkSize}MB`,
      "",
      "=== Connection Information ===",
      `Max requests: ${this.maxRequest}`,
      `Active sockets: ${this.sockets.length}`,
      `Sockets respawned: ${this.totalSocketsRespawned}`,
      `Server write threshold: ${this.serverWriteThreshold}MB`,
      `Connection retries: ${this.connectionRetries}`,
      `Max connection retries: ${this.maxConnectionRetries}`,
      `Close file retries: ${this.closeFileRetries}`,
      `Max close file retries: ${this.maxCloseFileRetries}`,
      "",
      "=== Upload Statistics ===",
      `Upload start time: ${
        this.uploadStartTime
          ? new Date(this.uploadStartTime).toLocaleString()
          : "N/A"
      }`,
      `Elapsed time: ${
        this.chunksInfo.elapsedTime
          ? Math.floor(this.chunksInfo.elapsedTime / 1000)
          : 0
      } seconds`,
      `Send chunks call count: ${this.sendChunksCallCount}`,
      `Send chunks rejections:`,
      `  - No idle socket: ${this.uploadSummary.sendChunksRejections.noIdleSocket}`,
      `  - No ready chunk: ${this.uploadSummary.sendChunksRejections.noReadyChunk}`,
      `  - Server write threshold exceeded: ${this.uploadSummary.sendChunksRejections.serverWriteThresholdExceeded}`,
      "",
      "=== Socket Failures ===",
      ...this.uploadSummary.socketFailures.map(
        (failure) =>
          `Socket ${failure.socketIndex} ${failure.type} at ${failure.timestamp}\n` +
          `  Chunks being sent: ${failure.details.socket.chunksBeingSent}\n` +
          `  Chunks acked: ${failure.details.socket.chunksAckd}\n` +
          `  Buffer amount: ${failure.details.socket.bufferedAmount}`
      ),
      "========================",
    ].join("\n");

    return debugText;
  };
}

export default WSUploads;
